import React from 'react';
import { Data } from '../../types/data-providers';
import { useData, dataFromDataset } from './DataProviderCache';
import * as SiteManager from '../domain/site-manager';
import { useAuthHelper } from '../../components/AuthContextProvider';
import { PromiseType } from '../../types/general';
import { useSelector } from 'react-redux';
import { Selectors } from '../../selectors';

type ContextState = Data<TrendData>;
type TrendData = PromiseType<ReturnType<typeof SiteManager.getTrendData>>;

const Context = React.createContext<ContextState | null>(null);

function useTrendData(): ContextState {
    const contextState = React.useContext(Context);
    if (contextState === null) {
        throw new Error('useTrendData must be used within a TrendDataProvider');
    }
    return contextState;
}

const TrendDataProvider: React.FC<{ siteId: string }> = (props) => {
    const authHelper = useAuthHelper();
    const days = useSelector(Selectors.Dashboard.trendCardDays);

    const { dataset } = useData({
        cacheKey: 'trend-data',
        id: `${props.siteId}-${days}`,
        dataFn: React.useCallback((): Promise<TrendData> => SiteManager.getTrendData(authHelper, props.siteId, days), [
            props.siteId,
            authHelper,
            days,
        ]),
    });

    return <Context.Provider value={dataFromDataset(dataset)}>{props.children}</Context.Provider>;
};

const TestTrendDataProvider: React.FC<{ value: ContextState }> = (props) => (
    <Context.Provider value={props.value}>{props.children}</Context.Provider>
);

export { TrendDataProvider, TestTrendDataProvider, useTrendData };
